import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  Truck, Power, Coffee, AlertTriangle, RefreshCw, CheckCircle2,
  X, Clock, Zap, MessageSquare, Hand, Heart, MapPin, Fuel,
  HardHat, ChevronRight, ArrowLeft, LogOut, Loader2,
  ShieldCheck, Send, ChevronDown, Users, BellRing, Droplet
} from "lucide-react";

const CHECKLIST_ITEMS = [
  { key: "mirrors", icon: ShieldCheck },
  { key: "tires", icon: ShieldCheck },
  { key: "fuel_check", icon: Fuel },
  { key: "documents", icon: ShieldCheck },
  { key: "safety_gear", icon: ShieldCheck },
];

export default function DriverDashboard() {
  const { session, hubData, refreshData, logout } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const nav = useNavigate();
  const { driverName: paramDriver } = useParams();
  const driverName = paramDriver || session?.driverName || "Driver";

  const { sites, machines, drivers, requests, events, messages } = hubData;
  const driver = drivers.find(d => d.driver_name === driverName);
  const isActive = driver?.status === "ACTIVE";

  const [toast, setToast] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [showMessages, setShowMessages] = useState(false);
  const [deliveryModal, setDeliveryModal] = useState(null);
  const [dieselAmount, setDieselAmount] = useState(200);
  const [adblueAmount, setAdblueAmount] = useState(0);
  const [showDriverSwitch, setShowDriverSwitch] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSiteId, setFeedbackSiteId] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };
  const allChecked = CHECKLIST_ITEMS.every(item => checklist[item.key]);

  const myMessages = useMemo(() =>
    messages.filter(m => m.to_user === driverName || m.from_user === driverName)
  , [messages, driverName]);
  const unreadCount = myMessages.filter(m => m.to_user === driverName && !m.read).length;

  const myRequests = useMemo(() =>
    requests.filter(r => r.assigned_driver === driverName && r.status !== "DONE")
  , [requests, driverName]);

  const mySites = useMemo(() =>
    sites.filter(s => s.main_driver === driverName || myRequests.some(r => r.site_id === s.id))
  , [sites, driverName, myRequests]);

  const sessionHours = useMemo(() => {
    if (!isActive || !driver?.sessions?.length) return null;
    const last = driver.sessions[driver.sessions.length - 1];
    if (last.end) return null;
    const diff = Date.now() - new Date(last.start).getTime();
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${h}h${String(m).padStart(2, "0")}`;
  }, [isActive, driver]);

  const handleShiftToggle = async () => {
    await api.toggleShift(driverName, session.hubId, isActive ? "stop" : "start");
    showToast(isActive ? t("stop_day") : t("start_day"));
    refreshData();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    showToast(t("data_synced"));
    setIsRefreshing(false);
  };

  const handleBreak = async () => {
    await api.createEvent({ hub_id: session.hubId, event_type: "DRIVER_BREAK", driver_name: driverName, message: `${driverName} taking a break` });
    showToast(t("pause"));
    refreshData();
  };

  const handleBreakdown = async () => {
    await api.createEvent({ hub_id: session.hubId, event_type: "DRIVER_BREAKDOWN", driver_name: driverName, message: `${driverName} reported a breakdown` });
    showToast(t("breakdown") + "!");
    refreshData();
  };

  const handleDelivery = async () => {
    if (!deliveryModal) return;
    await api.confirmDelivery({
      machine_id: deliveryModal.machine_id, site_id: deliveryModal.site_id,
      diesel: dieselAmount, adblue: adblueAmount, driver_name: driverName
    });
    showToast(t("confirm_delivery") + "!");
    setDeliveryModal(null);
    setDieselAmount(200);
    setAdblueAmount(0);
    refreshData();
  };

  const handleHighFive = async (to) => {
    await api.sendHighFive({ from_user: driverName, to_user: to, hub_id: session.hubId });
    showToast(t("high_five_sent"));
    refreshData();
  };

  const handleMarkRead = async (msgId) => {
    await api.markRead(msgId);
    refreshData();
  };

  const handleSendFeedback = async () => {
    if (!feedbackText.trim() || !feedbackSiteId) return;
    await api.sendFeedback({ hub_id: session.hubId, site_id: feedbackSiteId, message: feedbackText, author: driverName, feedback_type: "DRIVER" });
    showToast(t("feedback") + " sent!");
    setFeedbackText("");
    setFeedbackSiteId(null);
    refreshData();
  };

  const handleSwitchDriver = (name) => {
    setShowDriverSwitch(false);
    nav(`/driver/${name}`);
  };

  return (
    <div className="max-w-lg mx-auto min-h-[100dvh] bg-slate-950 pb-32 relative" data-testid="driver-dashboard">
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 left-4 right-4 z-[1000] bg-lime-500 text-slate-950 px-5 py-3 rounded-md shadow-lg flex items-center gap-2 font-bold text-sm uppercase tracking-wider animate-slideInDown max-w-lg mx-auto">
          <CheckCircle2 className="w-4 h-4" /> {toast}
        </div>
      )}

      {/* Delivery Modal */}
      {deliveryModal && (
        <div className="fixed inset-0 z-[200] bg-slate-950/95 flex items-center justify-center p-4 animate-fadeIn" data-testid="delivery-modal">
          <div className="bg-slate-900 rounded-md w-full max-w-sm border border-slate-800 animate-scaleIn">
            <div className="bg-slate-950 p-5 flex justify-between items-center border-b border-slate-800">
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-slate-100">{t("confirm_delivery")}</h3>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-1">{deliveryModal.machine_number} / {deliveryModal.site_name}</p>
              </div>
              <button onClick={() => setDeliveryModal(null)} className="p-2 bg-slate-800 rounded-md text-slate-400 hover:text-white" data-testid="close-delivery-modal">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">{t("diesel_liters")}</label>
                <input data-testid="diesel-input" type="number" value={dieselAmount} onChange={e => setDieselAmount(parseInt(e.target.value) || 0)}
                  className="w-full p-4 bg-slate-950 border border-slate-800 rounded-md text-xl font-black text-slate-100 font-mono text-center focus:border-lime-500/50 outline-none" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">{t("adblue_liters")}</label>
                <input data-testid="adblue-input" type="number" value={adblueAmount} onChange={e => setAdblueAmount(parseInt(e.target.value) || 0)}
                  className="w-full p-4 bg-slate-950 border border-slate-800 rounded-md text-xl font-black text-slate-100 font-mono text-center focus:border-lime-500/50 outline-none" />
              </div>
              <button data-testid="confirm-delivery-btn" onClick={handleDelivery}
                className="btn-industrial w-full py-4 bg-lime-500 text-slate-950 rounded-md text-sm font-black shadow-[0_0_20px_rgba(132,204,22,0.3)]">
                <Zap className="w-5 h-5" /> {t("deliver")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Driver Switch Modal */}
      {showDriverSwitch && (
        <div className="fixed inset-0 z-[200] bg-slate-950/95 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-slate-900 rounded-md w-full max-w-sm border border-slate-800 animate-scaleIn">
            <div className="bg-slate-950 p-5 flex justify-between items-center border-b border-slate-800">
              <h3 className="text-lg font-black uppercase tracking-tight text-slate-100">{t("switch_driver")}</h3>
              <button onClick={() => setShowDriverSwitch(false)} className="p-2 bg-slate-800 rounded-md text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-2">
              {drivers.map(d => (
                <button key={d.driver_name} onClick={() => handleSwitchDriver(d.driver_name)}
                  className={`w-full p-4 rounded-md flex items-center justify-between border transition-all ${d.driver_name === driverName ? "bg-lime-500/10 border-lime-500/40 text-lime-400" : "bg-slate-950 border-slate-800 text-slate-300 hover:border-slate-600"}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center font-black text-lg ${d.driver_name === driverName ? "bg-lime-500 text-slate-950" : "bg-slate-700 text-white"}`}>
                      {d.driver_name[0]}
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-bold uppercase tracking-tight block">{d.driver_name}</span>
                      <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{d.region}</span>
                    </div>
                  </div>
                  {d.driver_name === driverName ? <CheckCircle2 className="w-5 h-5" /> : <ChevronRight className="w-4 h-4 text-slate-600" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Messages Panel */}
      {showMessages && (
        <div className="fixed inset-0 z-[150] bg-slate-950 flex flex-col animate-slideInRight" data-testid="messages-panel">
          <header className="p-5 border-b border-slate-800 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-600 rounded-md text-white"><MessageSquare className="w-5 h-5" /></div>
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-slate-100">{t("messages")}</h3>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{t("info_hub")}</p>
              </div>
            </div>
            <button onClick={() => setShowMessages(false)} className="p-2.5 bg-slate-800 rounded-md text-slate-400 hover:text-white" data-testid="close-messages">
              <X className="w-5 h-5" />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {/* Pings */}
            <div className="space-y-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2"><BellRing className="w-3.5 h-3.5 text-lime-500" /> {t("messages")}</p>
              {myMessages.length === 0 ? (
                <p className="text-xs text-slate-600 text-center py-10 font-bold uppercase tracking-wider">{t("no_data")}</p>
              ) : myMessages.map(msg => (
                <div key={msg.id} onClick={() => !msg.read && msg.to_user === driverName && handleMarkRead(msg.id)}
                  className={`p-4 rounded-md border transition-all ${msg.read || msg.from_user === driverName ? "bg-slate-900 border-slate-800 opacity-70" : "bg-lime-500/10 border-lime-500/30"}`}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">{msg.from_user}</span>
                    <span className="text-[9px] font-mono text-slate-600">{new Date(msg.timestamp).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                  </div>
                  <p className="text-sm font-bold text-slate-200">{msg.message}</p>
                </div>
              ))}
            </div>
            {/* High-Five Wall */}
            <div className="space-y-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2"><Hand className="w-3.5 h-3.5 text-amber-500" /> {t("high_five_wall")}</p>
              <div className="p-5 bg-slate-900 border border-slate-800 rounded-md">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">{t("my_rating")}</span>
                    <div className="flex items-center gap-2 mt-1">
                      <Hand className="w-5 h-5 text-amber-500" />
                      <span className="text-2xl font-black text-slate-100">{driver?.high_five_count || 0}</span>
                    </div>
                  </div>
                  <button onClick={() => handleHighFive("Planner")} data-testid="give-planner-highfive"
                    className="px-4 py-2 bg-amber-500 text-slate-950 rounded-md text-xs font-bold uppercase flex items-center gap-2 active:scale-95 transition-all">
                    <Hand className="w-3.5 h-3.5" /> {t("give_planner")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-slate-950 p-4 border-b border-slate-800 sticky top-0 z-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-600 rounded-md flex items-center justify-center text-white shadow-[0_0_15px_rgba(220,38,38,0.3)]">
            <Truck className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-100">{t("driver_dashboard")}</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500">{driverName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="driver-messages-btn" onClick={() => setShowMessages(true)}
            className={`relative p-2.5 rounded-md transition-all border ${unreadCount > 0 ? "bg-lime-500/10 border-lime-500/30 text-lime-400" : "bg-slate-800 border-slate-700 text-slate-400"}`}>
            <MessageSquare className="w-4 h-4" />
            {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[8px] font-black flex items-center justify-center rounded-full">{unreadCount}</span>}
          </button>
          <button data-testid="driver-switch-btn" onClick={() => setShowDriverSwitch(true)}
            className="p-2.5 bg-slate-800 border border-slate-700 rounded-md text-slate-400 hover:text-white transition-all flex items-center gap-1">
            <Users className="w-4 h-4" /><ChevronDown className="w-3 h-3" />
          </button>
          <button data-testid="driver-shift-btn" onClick={handleShiftToggle}
            className={`p-2.5 rounded-md transition-all border ${isActive ? "bg-red-600 border-red-500 text-white shadow-[0_0_15px_rgba(220,38,38,0.3)]" : "bg-slate-800 border-slate-700 text-slate-400"}`}>
            <Power className="w-4 h-4" />
          </button>
          <button data-testid="driver-exit-btn" onClick={() => { logout(); nav("/"); }}
            className="p-2.5 bg-red-600/10 border border-red-600/20 rounded-md text-red-500 hover:bg-red-600 hover:text-white transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="p-4 space-y-5">
        {/* Shift Status */}
        <div className={`p-5 rounded-md border transition-all ${isActive ? "bg-slate-900 border-lime-500/20" : "bg-slate-900 border-slate-800 opacity-70"}`} data-testid="shift-status">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-md ${isActive ? "bg-lime-500/10 text-lime-400" : "bg-slate-800 text-slate-500"}`}>
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-100 uppercase tracking-tight">{t("day_status")}</h3>
                <p className={`text-[10px] font-bold ${isActive ? "text-lime-400" : "text-slate-500"}`}>
                  {isActive ? `${t("active")} ${sessionHours ? `/ ${sessionHours}` : ""}` : t("offline")}
                </p>
              </div>
            </div>
            {isActive && <div className="status-dot online" />}
          </div>
          <button data-testid="toggle-shift-btn" onClick={handleShiftToggle}
            className={`btn-industrial w-full py-4 rounded-md text-sm font-black ${isActive ? "bg-red-600 text-white" : "bg-lime-500 text-slate-950 shadow-[0_0_20px_rgba(132,204,22,0.3)]"}`}>
            <Power className="w-5 h-5" /> {isActive ? t("stop_day") : t("start_day")}
          </button>
        </div>

        {/* Quick Actions */}
        {isActive && (
          <div className="grid grid-cols-2 gap-3" data-testid="quick-actions">
            <button data-testid="break-btn" onClick={handleBreak}
              className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-md flex flex-col items-center gap-2 hover:bg-amber-500/20 transition-all active:scale-95">
              <Coffee className="w-6 h-6 text-amber-500" />
              <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">{t("pause")}</span>
            </button>
            <button data-testid="breakdown-btn" onClick={handleBreakdown}
              className="p-4 bg-red-600/10 border border-red-600/20 rounded-md flex flex-col items-center gap-2 hover:bg-red-600/20 transition-all active:scale-95">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">{t("breakdown")}</span>
            </button>
          </div>
        )}

        {/* Checklist */}
        <div className="bg-slate-900 border border-slate-800 rounded-md p-5" data-testid="checklist">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2 mb-4">
            <ShieldCheck className="w-4 h-4 text-lime-500" /> {t("checklist")}
          </h3>
          <div className="space-y-2">
            {CHECKLIST_ITEMS.map(item => (
              <button key={item.key} onClick={() => setChecklist(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                data-testid={`check-${item.key}`}
                className={`w-full p-3 rounded-md flex items-center justify-between border transition-all ${checklist[item.key] ? "bg-lime-500/10 border-lime-500/30 text-lime-400" : "bg-slate-950 border-slate-800 text-slate-400"}`}>
                <span className="text-xs font-bold uppercase tracking-wider">{t(item.key)}</span>
                {checklist[item.key] ? <CheckCircle2 className="w-4 h-4 text-lime-400" /> : <div className="w-4 h-4 rounded border border-slate-600" />}
              </button>
            ))}
          </div>
        </div>

        {/* Planning / Requests */}
        <div data-testid="planning-section">
          <div className="flex items-center justify-between mb-3 px-1">
            <h3 className="text-sm font-black text-slate-100 uppercase tracking-tight">{t("my_planning")}</h3>
            <button onClick={handleRefresh} className={`text-slate-500 hover:text-lime-400 ${isRefreshing ? "animate-spin-slow" : ""}`}>
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {mySites.length === 0 ? (
            <div className="py-16 text-center border border-dashed border-slate-800 rounded-md">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">{t("no_planning")}</p>
            </div>
          ) : mySites.map(site => {
            const siteReqs = myRequests.filter(r => r.site_id === site.id);
            return (
              <div key={site.id} className="bg-slate-900 border border-slate-800 rounded-md p-4 mb-3" data-testid={`driver-site-${site.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <MapPin className="w-4 h-4 text-red-500" />
                  <div>
                    <h4 className="text-sm font-bold text-slate-100 uppercase tracking-tight">{site.name}</h4>
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{site.city}</span>
                  </div>
                </div>

                {siteReqs.map(req => (
                  <div key={req.id} className={`p-3 border rounded-md mb-2 flex items-center justify-between ${req.urgency === "URGENT" ? "border-red-600/30 bg-red-600/5" : "border-slate-800 bg-slate-950"}`}>
                    <div>
                      <span className="text-xs font-bold text-slate-200">{req.machine_number}</span>
                      <div className="flex items-center gap-2 mt-1">
                        {req.products?.includes("DIESEL") && <span className="text-[9px] font-bold bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded">DIESEL</span>}
                        {req.products?.includes("ADBLUE") && <span className="text-[9px] font-bold bg-sky-500/10 text-sky-400 px-1.5 py-0.5 rounded">ADBLUE</span>}
                        {req.urgency === "URGENT" && <span className="text-[9px] font-bold bg-red-600/10 text-red-400 px-1.5 py-0.5 rounded animate-pulse">URGENT</span>}
                        {req.is_fun_request && <span className="text-[9px] font-bold bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded">FUN</span>}
                      </div>
                      {req.is_fun_request && <p className="text-[11px] text-amber-400 italic mt-1">{req.fun_message}</p>}
                    </div>
                    <button data-testid={`deliver-${req.id}`}
                      onClick={() => setDeliveryModal({ machine_id: req.machine_id, site_id: req.site_id, machine_number: req.machine_number, site_name: site.name })}
                      disabled={!isActive || !allChecked}
                      className="px-4 py-2 bg-lime-500 text-slate-950 rounded-md text-xs font-bold uppercase disabled:opacity-30 active:scale-95 transition-all flex items-center gap-1">
                      <Fuel className="w-3.5 h-3.5" /> {t("deliver")}
                    </button>
                  </div>
                ))}

                {/* Site Feedback */}
                <div className="mt-3 pt-3 border-t border-slate-800">
                  {feedbackSiteId === site.id ? (
                    <div className="flex gap-2">
                      <input value={feedbackText} onChange={e => setFeedbackText(e.target.value)}
                        placeholder={t("feedback_placeholder")}
                        className="flex-1 p-2 bg-slate-950 border border-slate-800 rounded text-xs text-slate-100 outline-none focus:border-lime-500/40" />
                      <button onClick={handleSendFeedback} disabled={!feedbackText.trim()}
                        className="px-3 py-2 bg-lime-500 text-slate-950 rounded text-xs font-bold disabled:opacity-30 active:scale-95">
                        <Send className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setFeedbackSiteId(null)} className="px-2 text-slate-500"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <button onClick={() => setFeedbackSiteId(site.id)}
                      className="text-[10px] font-bold text-slate-500 uppercase tracking-wider hover:text-lime-400 transition-colors flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> {t("feedback")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
